import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { store } from '../store';
import { updateBackgroundInfo } from '../redux/slices/userProfileSlice';
import { Admin_SaveUserDetail } from '../constants/adminData';
import { getSensitiveData, saveSensitiveData } from '../utils/secureStorage';

export interface SaveToast {
  message: string;
  type: 'success' | 'error' | 'warning';
}

export const useSaveUserProfile = () => {
  const dispatch = useDispatch();
  const [toast, setToast] = useState<SaveToast | null>(null);

  const clearToast = () => setToast(null);

  const mapReduxToApiPayload = async (state: any) => {
    // Read userId from store at call-time to avoid stale closure
    const currentProfile = store.getState().userProfile;
    return {
      userId: currentProfile?.userId || (await getSensitiveData('userId')),
      username: state.email,

      firstName: state.personalInfo.firstName || '',
      lastName: state.personalInfo.lastName || '',
      dateOfBirth: state.personalInfo.dateOfBirth || '1900-01-01',

      currentLocation: state.professionalInfo.location?.displayName || '',

      shortBio: state.backgroundInfo.shortBio || '',

      specialtyId: Number(state.professionalInfo.healthcareProfession) || null,
      hospitalAffiliation: state.professionalInfo.hospitalAffiliation || null,
      npiNumber: state.professionalInfo.npiNumber || null,

      genderId: Number(state.personalInfo.gender) || null,

      heightInches: Number(state.personalInfo.heightInches) || null,
      heightFeet: Number(state.personalInfo.heightFeet) || null,
      heightMeters: Number(state.personalInfo.heightMeters) || null,
      heightCentimeters: Number(state.personalInfo.heightCentimeters) || null,
      heightUnitSystem: state.personalInfo.heightUnitSystem || null,
      weightLbs: Number(state.personalInfo.weight) || null,

      religionId: Number(state.backgroundInfo.religionId) || null,
      ethnicityId: Number(state.backgroundInfo.ethnicityId) || null,
      maritalStatusId: Number(state.backgroundInfo.maritalStatusId) || null,

      workShiftId: Number(state.professionalInfo.workShiftID) || null,

      itSpecialtyId: Number(state.professionalInfo.itSpecialtyId) || null,
      currentOranizationName: state.professionalInfo.currentOranizationName,
      workTypeId: Number(state.professionalInfo.workType) || null,
      totalExperienceInYears: Number(state.professionalInfo.totalExperience) || 0,
      totalExperienceInMonths: 0,
      totalCompaniesHaveBeen: Number(state.professionalInfo.organizationCount) || 0,

      skills:
        state.backgroundInfo.skills?.map(
          (s: { skillId?: number; experience?: number }) => ({
            skillId: s.skillId ?? 0,
            experience: s.experience ?? 0,
          })
        ) || [],
      interestIds:
        state.backgroundInfo.interests?.map(
          (x: { interestId: number }) => x.interestId
        ) || [],

      availabilities:
        state.professionalInfo.availabilities?.map((availability: any) => ({
          ...availability,
          approvalStatus: 'Pending',
        })) || [],

      location: {
        displayName: state.professionalInfo.location?.displayName || '',
        latitude: state.professionalInfo.location?.latitude ?? 0,
        longitude: state.professionalInfo.location?.longitude ?? 0,
        city: state.professionalInfo.location?.city || '',
        state: state.professionalInfo.location?.state || '',
        country: state.professionalInfo.location?.country || '',
      },
    };
  };

  const saveUserProfile = async (shortBio?: string) => {
    // Always read the LATEST state from the store at call-time,
    // not from a stale render-time snapshot
    const latestProfile = store.getState().userProfile;
    const latestAuthToken = latestProfile.authToken;

    if (shortBio !== undefined) {
      dispatch(updateBackgroundInfo({ shortBio }));
    }

    const profileToSave = shortBio === undefined
      ? latestProfile
      : { ...latestProfile, backgroundInfo: { ...latestProfile.backgroundInfo, shortBio } };

    const payload = await mapReduxToApiPayload(profileToSave);

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'location' || key === 'skills' || key === 'interestIds' ||
    key === 'availabilities') return;
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    });

    formData.append('Location.DisplayName', payload.location.displayName);
    formData.append('location.Latitude', String(payload.location.latitude));
    formData.append('Location.Longitude', String(payload.location.longitude));
    formData.append('Location.City', String(payload.location.city));
    formData.append('Location.State', String(payload.location.state));
    formData.append('location.Country', String(payload.location.country));

    payload.skills?.forEach(
      (skill: { skillId: number; experience: number }, index: number) => {
        formData.append(`Skills[${index}].SkillId`, String(skill.skillId));
        formData.append(`Skills[${index}].Experience`, String(skill.experience));
      }
    );
    payload.interestIds?.forEach(
      (interestId: number, index: number) => {
        formData.append(
          `interestIds[${index}]`,
          String(interestId)
        );
      }
    );
    payload.availabilities?.forEach(
      (
        availability: {
          availabilityId?: number;
          availabilityTypeId?: number;
          availabilityTypeIds?: number[];
          availableHoursPerWeek: number;
          defaultStartDate?: string | null;
          defaultEndDate?: string | null;
          defaultAvailableHoursPerWeek?: number;
          preferredTimeSlot?: string | null;
          availableFromDate?: string | null;
          availableToDate?: string | null;
          notes?: string | null;
          isAvailable?: boolean;
          approvalStatus?: string;
          exceptions?: {
            availabilityExceptionId?: number;
            startDate: string;
            endDate: string;
            availableHoursPerWeek: number;
          }[];
        },
        index: number
      ) => {
        const availabilityTypeIds =
          availability.availabilityTypeIds ??
          (availability.availabilityTypeId ? [availability.availabilityTypeId] : []);

        formData.append(
          `Availabilities[${index}].AvailabilityId`,
          String(availability.availabilityId ?? 0)
        );

        formData.append(
          `Availabilities[${index}].IsAvailable`,
          String(availability.isAvailable ?? true)
        );

        formData.append(
          `Availabilities[${index}].AvailabilityTypeId`,
          String(availabilityTypeIds[0] ?? 0)
        );

        availabilityTypeIds.forEach((availabilityTypeId, typeIndex) => {
          formData.append(
            `Availabilities[${index}].AvailabilityTypeIds[${typeIndex}]`,
            String(availabilityTypeId)
          );
        });

        formData.append(
          `Availabilities[${index}].AvailableHoursPerWeek`,
          String(availability.availableHoursPerWeek)
        );

        formData.append(
          `Availabilities[${index}].DefaultStartDate`,
          availability.defaultStartDate || availability.availableFromDate || ''
        );

        formData.append(
          `Availabilities[${index}].DefaultEndDate`,
          availability.defaultEndDate || availability.availableToDate || ''
        );

        formData.append(
          `Availabilities[${index}].DefaultAvailableHoursPerWeek`,
          String(availability.defaultAvailableHoursPerWeek ?? availability.availableHoursPerWeek)
        );

        formData.append(
          `Availabilities[${index}].PreferredTimeSlot`,
          availability.preferredTimeSlot || ''
        );

        formData.append(
          `Availabilities[${index}].AvailableFromDate`,
          availability.availableFromDate || ''
        );

        formData.append(
          `Availabilities[${index}].AvailableToDate`,
          availability.availableToDate || ''
        );

        formData.append(
          `Availabilities[${index}].Notes`,
          availability.notes || ''
        );

        availability.exceptions?.forEach((exception, exceptionIndex) => {
          formData.append(
            `Availabilities[${index}].Exceptions[${exceptionIndex}].AvailabilityExceptionId`,
            String(exception.availabilityExceptionId ?? 0)
          );
          formData.append(
            `Availabilities[${index}].Exceptions[${exceptionIndex}].StartDate`,
            exception.startDate
          );
          formData.append(
            `Availabilities[${index}].Exceptions[${exceptionIndex}].EndDate`,
            exception.endDate
          );
          formData.append(
            `Availabilities[${index}].Exceptions[${exceptionIndex}].AvailableHoursPerWeek`,
            String(exception.availableHoursPerWeek)
          );
        });

        formData.append(
          `Availabilities[${index}].ApprovalStatus`,
          availability.approvalStatus || 'Pending'
        );
      }
    );

    if (latestProfile.personalInfo.profileImageFile) {
      formData.append('file', latestProfile.personalInfo.profileImageFile);
    }

    const response = await Admin_SaveUserDetail(formData, latestAuthToken);

    if (!response.success) {
      const message =
        response.errors?.[0] || response.message || 'Failed to save profile';
      setToast({ message, type: 'error' });
      throw new Error(message);
    }

    if (latestAuthToken) await saveSensitiveData('authToken', latestAuthToken);
    if (latestProfile.userId) await saveSensitiveData('userId', latestProfile.userId);

    setToast({ message: 'Profile saved successfully!', type: 'success' });
    return response;
  };

  return { saveUserProfile, toast, clearToast };
};
